<template>
  <el-dialog
    v-model="visible"
    title="任务查看权限矩阵"
    width="96%"
    top="4vh"
    destroy-on-close
    class="task-view-grant-dialog"
    @open="onOpen"
  >
    <el-alert
      type="info"
      :closable="false"
      show-icon
      class="grant-tip"
      title="说明"
      description="行=查看人（谁登录后想点进别人任务）；列=被查看人（任务责任人）。勾选一个格表示该查看人可进入该责任人的任务分类与列表。本人始终可查看自己。拥有「查看他人任务详情」或「团队管理」权限的用户不受本矩阵限制，可查看所有人。"
    />

    <div class="grant-toolbar">
      <el-input
        v-model="filterKeyword"
        placeholder="筛选姓名 / 用户名 / 部门"
        clearable
        style="width: 280px"
      />
      <el-button :loading="loading" @click="loadMatrix">刷新</el-button>
      <el-button type="primary" :loading="saving" @click="saveMatrix">保存矩阵</el-button>
    </div>

    <div v-loading="loading" class="grant-table-wrap">
      <table v-if="filteredViewers.length && targets.length" class="grant-table">
        <thead>
          <tr>
            <th class="grant-table__corner">查看人 ↓ / 被查看人 →</th>
            <th
              v-for="t in targets"
              :key="`h-${t.user_id}`"
              class="grant-table__col-head"
              :title="t.full_name"
            >
              {{ shortName(t) }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="v in filteredViewers" :key="`row-${v.user_id}`">
            <th class="grant-table__row-head" :title="v.full_name">
              {{ v.full_name || v.username }}
              <span v-if="viewerHasGlobal(v.user_id)" class="grant-table__global">全局</span>
            </th>
            <td
              v-for="t in targets"
              :key="`cell-${v.user_id}-${t.user_id}`"
              class="grant-table__cell"
              :class="{ 'is-self': Number(v.user_id) === Number(t.user_id) }"
            >
              <el-checkbox
                v-if="Number(v.user_id) !== Number(t.user_id)"
                :model-value="isGranted(v.user_id, t.user_id)"
                :disabled="viewerHasGlobal(v.user_id)"
                @change="(val) => setGranted(v.user_id, t.user_id, val)"
              />
              <span v-else class="grant-table__self">本人</span>
            </td>
          </tr>
        </tbody>
      </table>
      <el-empty v-else-if="!loading" description="暂无可用用户" />
    </div>
  </el-dialog>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { ElMessage } from 'element-plus'
import { getTaskViewGrantsMatrix, saveTaskViewGrantsMatrix } from '@/api/taskEvents'

const visible = defineModel({ type: Boolean, default: false })

const loading = ref(false)
const saving = ref(false)
const filterKeyword = ref('')
const viewers = ref([])
const targets = ref([])
const grantMap = ref({})
const globalViewerIds = ref(new Set())

const filteredViewers = computed(() => {
  const kw = filterKeyword.value.trim().toLowerCase()
  if (!kw) return viewers.value
  return viewers.value.filter((u) =>
    [u.full_name, u.username, u.department].some((x) =>
      String(x || '').toLowerCase().includes(kw)
    )
  )
})

function shortName(u) {
  const n = String(u.full_name || u.username || '').trim()
  return n.length > 4 ? `${n.slice(0, 4)}…` : n || '-'
}

function viewerHasGlobal(userId) {
  return globalViewerIds.value.has(Number(userId))
}

function isGranted(viewerId, targetId) {
  const key = String(viewerId)
  const list = grantMap.value[key] || []
  return list.includes(Number(targetId))
}

function setGranted(viewerId, targetId, checked) {
  const key = String(viewerId)
  const cur = new Set((grantMap.value[key] || []).map(Number))
  const tid = Number(targetId)
  if (checked) cur.add(tid)
  else cur.delete(tid)
  grantMap.value = { ...grantMap.value, [key]: [...cur] }
}

async function loadMatrix() {
  loading.value = true
  try {
    const res = await getTaskViewGrantsMatrix()
    const data = res?.data || {}
    viewers.value = data.viewers || []
    targets.value = data.targets || []
    grantMap.value = { ...(data.grants || {}) }
    globalViewerIds.value = new Set(
      (data.global_viewer_user_ids || []).map((id) => Number(id))
    )
  } catch (e) {
    ElMessage.error(e?.response?.data?.message || e?.message || '加载失败')
  } finally {
    loading.value = false
  }
}

async function saveMatrix() {
  saving.value = true
  try {
    const payload = {}
    for (const v of viewers.value) {
      if (viewerHasGlobal(v.user_id)) continue
      const key = String(v.user_id)
      payload[key] = (grantMap.value[key] || []).map(Number).filter((id) => id !== Number(v.user_id))
    }
    const res = await saveTaskViewGrantsMatrix(payload)
    if (res?.success) {
      ElMessage.success('已保存')
      visible.value = false
    } else {
      ElMessage.error(res?.message || '保存失败')
    }
  } catch (e) {
    ElMessage.error(e?.response?.data?.message || e?.message || '保存失败')
  } finally {
    saving.value = false
  }
}

function onOpen() {
  loadMatrix()
}

watch(visible, (v) => {
  if (v) loadMatrix()
})
</script>

<style scoped>
.grant-tip {
  margin-bottom: 12px;
}
.grant-toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
  margin-bottom: 12px;
}
.grant-table-wrap {
  max-height: 62vh;
  overflow: auto;
  border: 1px solid var(--el-border-color-lighter);
  border-radius: 6px;
}
.grant-table {
  border-collapse: collapse;
  font-size: 13px;
  min-width: 100%;
}
.grant-table th,
.grant-table td {
  border: 1px solid var(--el-border-color-lighter);
  padding: 6px 8px;
  text-align: center;
  white-space: nowrap;
}
.grant-table__corner {
  position: sticky;
  left: 0;
  z-index: 3;
  background: var(--el-fill-color-light);
  min-width: 140px;
  text-align: left;
}
.grant-table__row-head {
  position: sticky;
  left: 0;
  z-index: 2;
  background: #fff;
  text-align: left;
  min-width: 140px;
  font-weight: 500;
}
.grant-table__col-head {
  background: var(--el-fill-color-light);
  max-width: 72px;
  overflow: hidden;
  text-overflow: ellipsis;
}
.grant-table__cell.is-self {
  background: var(--el-fill-color-lighter);
}
.grant-table__self {
  color: var(--el-text-color-secondary);
  font-size: 12px;
}
.grant-table__global {
  display: inline-block;
  margin-left: 4px;
  font-size: 11px;
  color: var(--el-color-success);
}
</style>
